import { useEffect } from "react";
import { useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";
import { Highlighter } from "react-bootstrap-typeahead";

import Dropdown from "components/atoms/Dropdown/Dropdown";
import FormField from "components/molecules/FormField/FormField";
import { useGetCurrencies } from "api/queries/common/useGetCurrencies";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { ICurrencyOption } from "types/Common";
import { ReactComponent as CircleCross } from "assets/images/icons/CircleCross.svg";
import { COST_REGEX, handleOnKeyPressCost, getChargeTitleFromValue, renderAmountPerQuantity } from "helpers/costHelpers";
import { isValueExist } from "helpers/validationHelpers";

function CostTableRow({
  data,
  costIndex,
  title,
  onDelete,
  calculateCost,
  invoiceConfig,
  currentInvoice,
}: {
  title: string;
  data: any;
  costIndex: number;
  onDelete: Function;
  calculateCost: Function;
  invoiceConfig: any;
  currentInvoice: any;
}) {
  const isAdditionalInvoice = currentInvoice?.invoice_type === "additional_invoice" || false;
  const intl = useIntl();
  const specificationOptions = [
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.additionalCost",
        defaultMessage: "Additional cost",
      }),
      value: 1,
    },
    {
      label: intl.formatMessage({
        id: "tracking.components.additionalCostDialog.discount",
        defaultMessage: "Discount",
      }),
      value: 2,
    },
  ];

  const { setValue, watch } = useFormContext();

  const quotedDataFromInvoice = currentInvoice?.financial?.quoted_cost_items?.[getChargeTitleFromValue(title)]?.find(
    (cost: any) => cost.id === data.quoted_cost_id,
  );

  const { data: currencies = [] } = useGetCurrencies();
  const currencyOptions = currencies.map((currency) => ({
    key: currency.id,
    label: currency.code,
    name: currency.name,
    value: currency.code,
    code: currency.code,
  }));
  const { integration_config } = invoiceConfig || [];
  const integrationConfigCurrency = useWatch({ name: `invoice.provider_invoice_reference_03` });

  const [invoiceCurrencyCode, invoicedCost] = watch([
    `costs.${title}.${costIndex}.invoice_cost_currency_code`,
    `costs.${title}.${costIndex}.invoiced_cost`,
  ]);

  // Usually only maxeon would have config currency
  const currencyFromIntegrationConfig = integrationConfigCurrency ?? integration_config?.[0]?.currencies?.[0];

  useEffect(() => {
    // Exit if
    //  - there is no config currency
    if (!currencyFromIntegrationConfig) return;
    setValue(`costs.${title}.${costIndex}.invoice_cost_currency_code`, currencyFromIntegrationConfig);
  }, [currencyFromIntegrationConfig]);

  useEffect(() => {
    // Exit if
    //  - invoiceCurrencyCode is already set OR
    //  - there is no quoted currency
    //  - there is config currency
    if (invoiceCurrencyCode || !data.quoted_amount_currency_code || currencyFromIntegrationConfig) return;
    setValue(`costs.${title}.${costIndex}.invoice_cost_currency_code`, data.quoted_amount_currency_code);
  }, [data.quoted_amount_currency_code]);

  useEffect(() => {
    // Exit if
    //  - invoicedCost is already set OR
    //  - there is no quoted amount
    if (invoicedCost || !data.quoted_amount) return;
    setValue(`costs.${title}.${costIndex}.invoiced_cost`, data.quoted_amount);
  }, [data.quoted_amount]);

  const handleCurrencyRenderOption = ({ option, text }: { option: ICurrencyOption; text: string }) => (
    <div className="p-2">
      <div className="font-weight-bold font-12">
        <Highlighter search={text}>{`(${option.code}) ${option.name}`}</Highlighter>
      </div>
    </div>
  );

  const handleCurrencyChange = (currency: ICurrencyOption[]) => {
    if (currency[0]) setValue("currency", currency[0].code);
  };

  return (
    <tr className="h-46-px">
      <td>
        {!data.quoted_cost_id || !data.charge_name ? (
          <FormField
            validations={{
              shouldUnregister: true,
              validate: { valueExist: isValueExist },
            }}
            field_id={`costs.${title}.${costIndex}.charge_name`}
            mandatory
          />
        ) : (
          <div>{data.charge_name}</div>
        )}
      </td>
      <td>
        {!data.quoted_cost_id ? (
          <div>-</div>
        ) : (
          <div>
            <p className="m-0">
              {data.quoted_amount} {data.quoted_amount_currency_code}
            </p>
            <p className="m-0 text-tertiary" style={{ fontSize: "10px" }}>
              {quotedDataFromInvoice && renderAmountPerQuantity(quotedDataFromInvoice)}
            </p>
          </div>
        )}
      </td>
      <td>
        <div style={{ maxWidth: "120px" }}>
          <Dropdown
            name={`costs.${title}.${costIndex}.type`}
            options={specificationOptions}
            defaultValue={specificationOptions[0].value}
            useHookForm
            shouldUnregister
            onChange={calculateCost}
          />
        </div>
      </td>
      <td>
        <div className="w-92-px">
          <FormField
            mandatory
            field_id={`costs.${title}.${costIndex}.invoiced_cost`}
            validations={{
              shouldUnregister: true,
            }}
            validationPattern={{
              value: COST_REGEX,
              message: intl.formatMessage({ id: "general.invalidCost", defaultMessage: "Invalid cost" }),
            }}
            onKeyPress={(event) => handleOnKeyPressCost(event)}
          />
        </div>
      </td>
      <td>
        {integration_config?.length === 0 && (
          <div className="w-92-px">
            <SearchSelect
              name={`costs.${title}.${costIndex}.invoice_cost_currency_code`}
              options={currencyOptions}
              filterBy={["code", "name"]}
              placeholder={intl.formatMessage({ id: "general.currency", defaultMessage: "Currency" })}
              handleRenderOption={handleCurrencyRenderOption}
              onSelect={handleCurrencyChange}
              shouldUnregister
              required
            />
          </div>
        )}
        {integration_config?.length > 0 && (
          <div className="w-92-px">
            <Dropdown
              name={`costs.${title}.${costIndex}.invoice_cost_currency_code`}
              options={currencyOptions}
              useHookForm
              disabled
              shouldUnregister
            />
          </div>
        )}
      </td>
      {(!data.quoted_cost_id || isAdditionalInvoice) && (
        <td>
          <CircleCross
            className="cursor-pointer"
            onClick={() => {
              onDelete(costIndex);
            }}
          />
        </td>
      )}
    </tr>
  );
}

export default CostTableRow;
